import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import MainLayout from '../../components/layout/MainLayout';
import BackButton from '../../components/common/BackButton';
import billingApi, { Subscription, Invoice, UsageData, PaymentMethod } from '../../services/api/billing.api';
import BillingPeriodSelector, { BillingPeriod, getCurrentMonthPeriod } from '../../components/billing/BillingPeriodSelector';
import {
  CheckIcon,
  CreditCardIcon,
  SparklesIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  ServerIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';

interface PayAsYouGoData {
  usage: {
    avgInfraHosts: number;
    avgAPMHosts: number;
    spanIngestionGB: number;
    indexedSpansCount: number;
    logIngestionGB: number;
    indexedLogEventsCount: number;
    customMetricsCount: number;
    rumSessionsCount: number;
    rumEventsCount: number;
    sessionRecordingsCount: number;
    recordingStorageGB: number;
    cloudScansCount: number;
    uptimeMonitorsCount: number;
    securityScansCount: number;
    seoScansCount: number;
    aiInputTokensCount: number;
    aiOutputTokensCount: number;
  };
  costs: {
    baseCost: number;
    infraHostsCost: number;
    apmHostsCost: number;
    spanIngestionCost: number;
    indexedSpansCost: number;
    logIngestionCost: number;
    indexedLogsCost: number;
    customMetricsCost: number;
    rumSessionsCost: number;
    rumEventsCost: number;
    sessionRecordingsCost: number;
    recordingStorageCost: number;
    infraHostsUsed: number;
    infraHostsIncluded: number;
    infraHostsBillable: number;
    apmHostsUsed: number;
    apmHostsIncluded: number;
    apmHostsBillable: number;
    spanIngestionUsedGB: number;
    spanIngestionIncludedGB: number;
    spanIngestionBillableGB: number;
    indexedSpansUsed: number;
    indexedSpansIncluded: number;
    indexedSpansBillable: number;
    logIngestionUsedGB: number;
    logIngestionIncludedGB: number;
    logIngestionBillableGB: number;
    indexedLogsUsed: number;
    indexedLogsIncluded: number;
    indexedLogsBillable: number;
    customMetricsUsed: number;
    customMetricsIncluded: number;
    customMetricsBillable: number;
    rumSessionsUsed: number;
    rumSessionsIncluded: number;
    rumSessionsBillable: number;
    rumEventsUsed: number;
    rumEventsIncluded: number;
    rumEventsBillable: number;
    sessionRecordingsUsed: number;
    sessionRecordingsIncluded: number;
    sessionRecordingsBillable: number;
    recordingStorageUsedGB: number;
    recordingStorageIncludedGB: number;
    recordingStorageBillableGB: number;
    cloudScansUsed: number;
    cloudScansIncluded: number;
    cloudScansBillable: number;
    cloudScansCost: number;
    uptimeMonitorsUsed: number;
    uptimeMonitorsIncluded: number;
    uptimeMonitorsBillable: number;
    uptimeMonitorsCost: number;
    securityScansUsed: number;
    securityScansIncluded: number;
    securityScansBillable: number;
    securityScansCost: number;
    seoScansUsed: number;
    seoScansIncluded: number;
    seoScansBillable: number;
    seoScansCost: number;
    aiTokensUsed: number;
    aiTokensIncluded: number;
    aiTokensBillable: number;
    aiInputTokensUsed: number;
    aiOutputTokensUsed: number;
    aiTokensCost: number;
    totalCost: number;
  };
  plan: {
    id: string;
    name: string;
    tier: string;
    price_monthly: number;
    price_annual: number;
  };
  subscription: {
    id: string;
    status: string;
    billing_cycle: string;
    trial_ends_at: string | null;
    current_period_start: string;
    current_period_end: string;
  };
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

export default function BillingPage() {
  const { t, i18n } = useTranslation('billing');

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [paygData, setPaygData] = useState<PayAsYouGoData | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dailyCosts, setDailyCosts] = useState<{ date: string; cost: number }[]>([]);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(() => getCurrentMonthPeriod(i18n.language));

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const startDate = billingPeriod.startDate.toISOString();
      const endDate = billingPeriod.endDate.toISOString();

      console.log('[BillingPage] Loading data for period:', { startDate, endDate, label: billingPeriod.label });

      const [subResponse, usageResponse, paygResponse, invoicesResponse, paymentMethodsResponse, dailyCostsResponse] = await Promise.all([
        billingApi.getCurrentSubscription().catch(() => ({ success: false, data: null })),
        billingApi.getCurrentUsage({ startDate, endDate }).catch(() => ({ success: false, data: null })),
        billingApi.getPayAsYouGoUsage({ startDate, endDate }).catch(() => ({ success: false, data: null })),
        billingApi.getInvoices({ limit: 10 }).catch(() => ({ success: false, data: { invoices: [], total: 0 } })),
        billingApi.getPaymentMethods().catch(() => ({ success: false, data: [] })),
        billingApi.getDailyCosts({ startDate, endDate }).catch(() => ({ success: false, data: [] })),
      ]);

      if (subResponse.success && subResponse.data) {
        setSubscription(subResponse.data);
      }
      if (usageResponse.success) setUsage(usageResponse.data);
      if (paygResponse.success && paygResponse.data) setPaygData(paygResponse.data);
      if (invoicesResponse.success) setInvoices(invoicesResponse.data.invoices);
      if (paymentMethodsResponse.success) setPaymentMethods(paymentMethodsResponse.data);
      if (dailyCostsResponse.success) setDailyCosts(dailyCostsResponse.data);
    } catch (error) {
      console.error('Error loading billing data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [billingPeriod]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenStripePortal = async () => {
    try {
      const response = await billingApi.createPortalSession();
      if (response.success) {
        window.open(response.data.url, '_blank');
      }
    } catch (error: any) {
      console.error('Portal error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to open billing portal.';
      toast.error(`${errorMessage}\n\nNote: The billing portal requires an active Stripe subscription. Please contact support to manage your subscription.`, {
        duration: 8000,
        icon: '❌',
      });
    }
  };

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (numPrice === 0 || isNaN(numPrice)) return 'Free';
    const currency = subscription?.tenant?.currency || subscription?.plan?.currency || 'USD';
    const locale = currency === 'BRL' ? 'pt-BR' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(numPrice);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getUsagePercentage = (used: number, limit: number) => {
    if (limit <= 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage < 75) return 'bg-green-500';
    if (percentage < 90) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getCardBrandIcon = (brand: string) => {
    const brands: Record<string, string> = {
      visa: '💳 Visa',
      mastercard: '💳 Mastercard',
      amex: '💳 Amex',
      discover: '💳 Discover',
    };
    return brands[brand.toLowerCase()] || '💳 ' + brand;
  };

  // Chart data for usage over time
  const usageChartData = usage && subscription ? {
    labels: [t('charts.metrics'), t('charts.logs'), t('charts.traces'), t('charts.storage')],
    datasets: [
      {
        label: t('charts.currentUsage'),
        data: [
          usage.metrics_events,
          usage.log_events,
          usage.trace_spans,
          usage.storage_gb * 1000000, // Convert to comparable scale
        ],
        borderColor: 'rgb(99, 102, 241)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        tension: 0.4,
      },
      {
        label: t('charts.planLimit'),
        data: [
          subscription.plan.included_metrics_events,
          subscription.plan.included_log_events,
          subscription.plan.included_trace_spans,
          subscription.plan.included_storage_gb * 1000000,
        ],
        borderColor: 'rgb(156, 163, 175)',
        backgroundColor: 'rgba(156, 163, 175, 0.1)',
        borderDash: [5, 5],
        tension: 0.4,
      },
    ],
  } : null;

  // Doughnut chart for overall usage
  const overallUsageData = usage && subscription ? {
    labels: ['Used', 'Remaining'],
    datasets: [
      {
        data: [
          usage.metrics_events + usage.log_events + usage.trace_spans,
          (subscription.plan.included_metrics_events + subscription.plan.included_log_events + subscription.plan.included_trace_spans) -
          (usage.metrics_events + usage.log_events + usage.trace_spans),
        ],
        backgroundColor: ['rgb(99, 102, 241)', 'rgb(229, 231, 235)'],
        borderWidth: 0,
      },
    ],
  } : null;

  // Cost breakdown chart for pay-as-you-go
  const costBreakdownData = paygData ? {
    labels: [
      t('basePlan'),
      t('metrics.infraHosts'),
      t('metrics.apmHosts'),
      t('metrics.spanIngestionShort'),
      t('metrics.indexedSpans'),
      t('metrics.logIngestionShort'),
      t('metrics.indexedLogs'),
      t('metrics.customMetrics'),
      t('metrics.rumSessions'),
      t('metrics.rumEvents'),
      t('metrics.sessionRecordings'),
      t('metrics.recordingStorageShort'),
      t('metrics.cloudScans'),
      t('metrics.uptimeMonitors'),
      t('metrics.securityScans'),
      t('metrics.seoScans'),
      t('metrics.aiTokens'),
    ],
    datasets: [
      {
        data: [
          paygData.costs.baseCost,
          paygData.costs.infraHostsCost,
          paygData.costs.apmHostsCost,
          paygData.costs.spanIngestionCost,
          paygData.costs.indexedSpansCost,
          paygData.costs.logIngestionCost,
          paygData.costs.indexedLogsCost,
          paygData.costs.customMetricsCost,
          paygData.costs.rumSessionsCost,
          paygData.costs.rumEventsCost,
          paygData.costs.sessionRecordingsCost,
          paygData.costs.recordingStorageCost,
          paygData.costs.cloudScansCost || 0,
          paygData.costs.uptimeMonitorsCost || 0,
          paygData.costs.securityScansCost || 0,
          paygData.costs.seoScansCost || 0,
          paygData.costs.aiTokensCost || 0,
        ],
        backgroundColor: [
          'rgb(99, 102, 241)',
          'rgb(34, 197, 94)',
          'rgb(234, 179, 8)',
          'rgb(239, 68, 68)',
          'rgb(168, 85, 247)',
          'rgb(236, 72, 153)',
          'rgb(14, 165, 233)',
          'rgb(251, 146, 60)',
          'rgb(59, 130, 246)',
          'rgb(139, 92, 246)',
          'rgb(236, 72, 153)',
          'rgb(20, 184, 166)',
          'rgb(99, 102, 241)',
          'rgb(20, 184, 166)',
          'rgb(239, 68, 68)',
          'rgb(34, 197, 94)',
          'rgb(168, 85, 247)',
        ],
        borderWidth: 2,
        borderColor: '#fff',
      },
    ],
  } : null;

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading billing information...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <BackButton />
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('title')}</h1>
          <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            {t('subtitle')}
          </p>
          <div className="mt-3 sm:mt-4">
            <BillingPeriodSelector value={billingPeriod} onChange={setBillingPeriod} />
          </div>
        </div>

        {/* Trial Banner */}
        {paygData?.subscription.status === 'TRIALING' && paygData?.subscription.trial_ends_at && (
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6 text-white">
            <div className="flex items-start sm:items-center gap-3">
              <ClockIcon className="h-6 w-6 sm:h-8 sm:w-8 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-semibold mb-1">🎉 Trial Period Active</h3>
                <p className="text-xs sm:text-sm opacity-90">
                  Your trial ends on {new Date(paygData.subscription.trial_ends_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}.
                  {paygData.costs.totalCost > paygData.costs.baseCost && (
                    <span className="block sm:inline sm:ml-1 mt-1 sm:mt-0">
                      Your projected monthly cost is {formatPrice(paygData.costs.totalCost)}.
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Current Usage Summary Card */}
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg p-4 sm:p-8 mb-6 sm:mb-8 text-white">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <SparklesIcon className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-medium opacity-90">{t('subscription.title')}</span>
              </div>
              <h2 className="text-2xl sm:text-4xl font-bold mb-2 truncate">
                {subscription?.plan?.name || t('subscription.payAsYouGo')}
              </h2>
              <p className="text-sm sm:text-lg opacity-90 mb-3 sm:mb-4">
                {t('subscription.usageBased')}
              </p>
              {subscription && paygData && (
                <div>
                  <div className="flex flex-wrap items-baseline gap-1 sm:gap-2 mb-2">
                    <span className="text-2xl sm:text-3xl font-bold">
                      {formatPrice(paygData.costs.totalCost)}
                    </span>
                    <span className="text-sm sm:text-lg opacity-75">
                      /month (current usage)
                    </span>
                  </div>
                  {paygData.costs.totalCost > paygData.costs.baseCost && (
                    <p className="text-xs sm:text-sm opacity-80">
                      Base: {formatPrice(paygData.costs.baseCost)} + Usage: {formatPrice(paygData.costs.totalCost - paygData.costs.baseCost)}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 sm:gap-3 w-full sm:w-auto">
              {subscription?.stripe_customer_id ? (
                <button
                  onClick={handleOpenStripePortal}
                  className="px-4 sm:px-6 py-2 sm:py-3 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/20 text-sm sm:text-base"
                >
                  <CreditCardIcon className="h-4 w-4 sm:h-5 sm:w-5 inline mr-1 sm:mr-2" />
                  {t('manageBilling')}
                </button>
              ) : (
                <div className="px-4 sm:px-6 py-2 sm:py-3 bg-white/5 backdrop-blur-sm text-white/70 rounded-lg border border-white/10 text-center">
                  <p className="text-xs sm:text-sm">{t('manualBilling')}</p>
                  <p className="text-xs mt-1 opacity-75 hidden sm:block">{t('contactSupportBilling')}</p>
                </div>
              )}
              {paygData && (
                <a
                  href={`https://flux.devskin.com/pricing`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 sm:px-6 py-2 sm:py-3 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/20 text-center text-sm sm:text-base"
                >
                  <ChartBarIcon className="h-4 w-4 sm:h-5 sm:w-5 inline mr-1 sm:mr-2" />
                  {t('estimateCosts')}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Pay-As-You-Go Cost Breakdown */}
        {paygData && (
          <div className="mb-6 sm:mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4 sm:mb-6">
                <div className="flex items-center gap-2">
                  <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">{t('usageBasedCostBreakdown')}</h3>
                </div>
                <span className="sm:ml-auto text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  {formatPrice(paygData.costs.totalCost)}
                </span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6">
                {/* Cost Breakdown Chart */}
                <div>
                  {costBreakdownData && (
                    <Doughnut
                      data={costBreakdownData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                          legend: {
                            display: true,
                            position: 'bottom',
                            labels: {
                              color: document.documentElement.classList.contains('dark') ? '#9CA3AF' : '#4B5563',
                              font: { size: 11 },
                            },
                          },
                          tooltip: {
                            callbacks: {
                              label: function (context) {
                                const label = context.label || '';
                                const value = context.parsed || 0;
                                return `${label}: ${formatPrice(value)}`;
                              },
                            },
                          },
                        },
                      }}
                    />
                  )}
                </div>

                {/* Cost Details Table */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="font-medium text-gray-900 dark:text-white">Base Plan ({paygData.plan.name})</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{formatPrice(paygData.costs.baseCost)}</span>
                  </div>

                  {paygData.costs.infraHostsCost > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        <ServerIcon className="h-4 w-4 inline mr-1" />
                        {t('metrics.infraHosts')} ({paygData.costs.infraHostsBillable.toFixed(1)} {t('billable')})
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.infraHostsCost)}</span>
                    </div>
                  )}

                  {paygData.costs.apmHostsCost > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        <ServerIcon className="h-4 w-4 inline mr-1" />
                        {t('metrics.apmHosts')} ({paygData.costs.apmHostsBillable.toFixed(1)} {t('billable')})
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.apmHostsCost)}</span>
                    </div>
                  )}

                  {paygData.costs.spanIngestionCost > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        Span Ingestion ({paygData.costs.spanIngestionBillableGB.toFixed(2)} GB)
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.spanIngestionCost)}</span>
                    </div>
                  )}

                  {paygData.costs.indexedSpansCost > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        Indexed Spans ({formatNumber(paygData.costs.indexedSpansBillable)})
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.indexedSpansCost)}</span>
                    </div>
                  )}

                  {paygData.costs.logIngestionCost > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        Log Ingestion ({paygData.costs.logIngestionBillableGB.toFixed(2)} GB)
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.logIngestionCost)}</span>
                    </div>
                  )}

                  {paygData.costs.indexedLogsCost > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        Indexed Logs ({formatNumber(paygData.costs.indexedLogsBillable)})
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.indexedLogsCost)}</span>
                    </div>
                  )}

                  {paygData.costs.customMetricsCost > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        Custom Metrics ({paygData.costs.customMetricsBillable})
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.customMetricsCost)}</span>
                    </div>
                  )}

                  {paygData.costs.rumSessionsCost > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        RUM Sessions ({formatNumber(paygData.costs.rumSessionsBillable)})
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.rumSessionsCost)}</span>
                    </div>
                  )}

                  {paygData.costs.rumEventsCost > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        RUM Events ({formatNumber(paygData.costs.rumEventsBillable)})
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.rumEventsCost)}</span>
                    </div>
                  )}

                  {paygData.costs.sessionRecordingsCost > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        Session Recordings ({paygData.costs.sessionRecordingsBillable})
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.sessionRecordingsCost)}</span>
                    </div>
                  )}

                  {paygData.costs.recordingStorageCost > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        {t('metrics.recordingStorageShort')} ({paygData.costs.recordingStorageBillableGB.toFixed(2)} GB)
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.recordingStorageCost)}</span>
                    </div>
                  )}

                  {paygData.costs.cloudScansCost > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        <ShieldCheckIcon className="h-4 w-4 inline mr-1" />
                        Cloud Scans ({paygData.costs.cloudScansBillable} scans)
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.cloudScansCost)}</span>
                    </div>
                  )}

                  {(paygData.costs.uptimeMonitorsCost || 0) > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        <ClockIcon className="h-4 w-4 inline mr-1" />
                        Uptime Monitors ({paygData.costs.uptimeMonitorsBillable} monitors)
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.uptimeMonitorsCost)}</span>
                    </div>
                  )}

                  {(paygData.costs.securityScansCost || 0) > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        <ShieldCheckIcon className="h-4 w-4 inline mr-1" />
                        Security Scans ({paygData.costs.securityScansBillable} scans)
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.securityScansCost)}</span>
                    </div>
                  )}

                  {(paygData.costs.seoScansCost || 0) > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        <ChartBarIcon className="h-4 w-4 inline mr-1" />
                        SEO Scans ({paygData.costs.seoScansBillable} scans)
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.seoScansCost)}</span>
                    </div>
                  )}

                  {(paygData.costs.aiTokensCost || 0) > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        <SparklesIcon className="h-4 w-4 inline mr-1" />
                        {t('metrics.aiTokens')} ({formatNumber(paygData.costs.aiTokensBillable)} tokens)
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatPrice(paygData.costs.aiTokensCost)}</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300 dark:border-gray-600">
                    <span className="font-bold text-gray-900 dark:text-white">Total (Month-to-Date)</span>
                    <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                      {formatPrice(paygData.costs.totalCost)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Usage Details with Progress Bars */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700">
                {/* Infrastructure Hosts */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      <ServerIcon className="h-4 w-4 inline mr-1" />
                      {t('metrics.infraHosts')}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {paygData.costs.infraHostsUsed.toFixed(1)} / {paygData.costs.infraHostsIncluded} {t('metrics.included')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.infraHostsUsed, paygData.costs.infraHostsIncluded)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.infraHostsUsed, paygData.costs.infraHostsIncluded), 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* APM Hosts */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      <ServerIcon className="h-4 w-4 inline mr-1" />
                      {t('metrics.apmHosts')}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {paygData.costs.apmHostsUsed.toFixed(1)} / {paygData.costs.apmHostsIncluded} {t('metrics.included')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.apmHostsUsed, paygData.costs.apmHostsIncluded)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.apmHostsUsed, paygData.costs.apmHostsIncluded), 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Span Ingestion */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('metrics.spanIngestion')}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {paygData.costs.spanIngestionUsedGB.toFixed(2)} / {paygData.costs.spanIngestionIncludedGB} GB included
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.spanIngestionUsedGB, paygData.costs.spanIngestionIncludedGB)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.spanIngestionUsedGB, paygData.costs.spanIngestionIncludedGB), 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Log Ingestion */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('metrics.logIngestion')}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {paygData.costs.logIngestionUsedGB.toFixed(2)} / {paygData.costs.logIngestionIncludedGB} GB included
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.logIngestionUsedGB, paygData.costs.logIngestionIncludedGB)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.logIngestionUsedGB, paygData.costs.logIngestionIncludedGB), 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Indexed Spans */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('metrics.indexedSpans')}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {formatNumber(paygData.costs.indexedSpansUsed)} / {formatNumber(paygData.costs.indexedSpansIncluded)} {t('metrics.included')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.indexedSpansUsed, paygData.costs.indexedSpansIncluded)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.indexedSpansUsed, paygData.costs.indexedSpansIncluded), 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Indexed Logs */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('metrics.indexedLogs')}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {formatNumber(paygData.costs.indexedLogsUsed)} / {formatNumber(paygData.costs.indexedLogsIncluded)} {t('metrics.included')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.indexedLogsUsed, paygData.costs.indexedLogsIncluded)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.indexedLogsUsed, paygData.costs.indexedLogsIncluded), 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Custom Metrics */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('metrics.customMetrics')}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {paygData.costs.customMetricsUsed} / {paygData.costs.customMetricsIncluded} {t('metrics.included')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.customMetricsUsed, paygData.costs.customMetricsIncluded)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.customMetricsUsed, paygData.costs.customMetricsIncluded), 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* RUM Sessions */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('metrics.rumSessions')}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {formatNumber(paygData.costs.rumSessionsUsed)} / {formatNumber(paygData.costs.rumSessionsIncluded)} {t('metrics.included')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.rumSessionsUsed, paygData.costs.rumSessionsIncluded)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.rumSessionsUsed, paygData.costs.rumSessionsIncluded), 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* RUM Events */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('metrics.rumEvents')}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {formatNumber(paygData.costs.rumEventsUsed)} / {formatNumber(paygData.costs.rumEventsIncluded)} {t('metrics.included')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.rumEventsUsed, paygData.costs.rumEventsIncluded)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.rumEventsUsed, paygData.costs.rumEventsIncluded), 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Session Recordings */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('metrics.sessionRecordings')}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {paygData.costs.sessionRecordingsUsed} / {paygData.costs.sessionRecordingsIncluded} {t('metrics.included')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.sessionRecordingsUsed, paygData.costs.sessionRecordingsIncluded)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.sessionRecordingsUsed, paygData.costs.sessionRecordingsIncluded), 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Recording Storage */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('metrics.recordingStorage')}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {paygData.costs.recordingStorageUsedGB.toFixed(2)} / {paygData.costs.recordingStorageIncludedGB} {t('gbIncluded')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.recordingStorageUsedGB, paygData.costs.recordingStorageIncludedGB)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.recordingStorageUsedGB, paygData.costs.recordingStorageIncludedGB), 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Cloud Scans */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      <ShieldCheckIcon className="h-4 w-4 inline mr-1" />
                      {t('metrics.cloudScans')}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {paygData.costs.cloudScansUsed} / {paygData.costs.cloudScansIncluded} {t('metrics.included')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.cloudScansUsed, paygData.costs.cloudScansIncluded)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.cloudScansUsed, paygData.costs.cloudScansIncluded), 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Uptime Monitors */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      <ClockIcon className="h-4 w-4 inline mr-1" />
                      {t('metrics.uptimeMonitors')}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {paygData.costs.uptimeMonitorsUsed || 0} / {paygData.costs.uptimeMonitorsIncluded || 0} {t('metrics.included')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.uptimeMonitorsUsed || 0, paygData.costs.uptimeMonitorsIncluded || 0)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.uptimeMonitorsUsed || 0, paygData.costs.uptimeMonitorsIncluded || 0), 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Security Scans */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      <ShieldCheckIcon className="h-4 w-4 inline mr-1" />
                      {t('metrics.securityScans')}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {paygData.costs.securityScansUsed || 0} / {paygData.costs.securityScansIncluded || 0} {t('metrics.included')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.securityScansUsed || 0, paygData.costs.securityScansIncluded || 0)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.securityScansUsed || 0, paygData.costs.securityScansIncluded || 0), 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* SEO Scans */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      <ChartBarIcon className="h-4 w-4 inline mr-1" />
                      {t('metrics.seoScans')}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {paygData.costs.seoScansUsed || 0} / {paygData.costs.seoScansIncluded || 0} {t('metrics.included')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.seoScansUsed || 0, paygData.costs.seoScansIncluded || 0)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.seoScansUsed || 0, paygData.costs.seoScansIncluded || 0), 100)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* AI Assistant Tokens */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      <SparklesIcon className="h-4 w-4 inline mr-1" />
                      {t('metrics.aiTokens')}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {formatNumber(paygData.costs.aiTokensUsed || 0)} tokens · {formatPrice(paygData.costs.aiTokensCost || 0)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${getUsageColor(
                        getUsagePercentage(paygData.costs.aiTokensUsed || 0, paygData.costs.aiTokensIncluded || 0)
                      )}`}
                      style={{
                        width: `${Math.min(getUsagePercentage(paygData.costs.aiTokensUsed || 0, paygData.costs.aiTokensIncluded || 0), 100)}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Daily Costs Chart */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600 dark:text-indigo-400" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">{t('dailyCosts')}</h3>
            </div>
            {dailyCosts.length > 0 ? (
              <div className="h-64 sm:h-80">
                <Bar
                  data={{
                    labels: dailyCosts.map((d) => {
                      const date = new Date(d.date);
                      return date.toLocaleDateString(i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US', { day: '2-digit', month: 'short' });
                    }),
                    datasets: [
                      {
                        label: t('cost'),
                        data: dailyCosts.map((d) => d.cost),
                        backgroundColor: 'rgba(99, 102, 241, 0.7)',
                        borderColor: 'rgb(99, 102, 241)',
                        borderWidth: 1,
                        borderRadius: 4,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                      tooltip: {
                        callbacks: {
                          label: (context) => `${formatPrice(context.parsed.y)}`,
                        },
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          color: document.documentElement.classList.contains('dark') ? '#9CA3AF' : '#6B7280',
                          callback: (value) => formatPrice(value as number),
                        },
                        grid: {
                          color: document.documentElement.classList.contains('dark') ? '#374151' : '#E5E7EB',
                        },
                      },
                      x: {
                        ticks: {
                          color: document.documentElement.classList.contains('dark') ? '#9CA3AF' : '#6B7280',
                          maxRotation: 45,
                          minRotation: 45,
                        },
                        grid: {
                          display: false,
                        },
                      },
                    },
                  }}
                />
              </div>
            ) : (
              <div className="h-64 sm:h-80 flex items-center justify-center">
                <div className="text-center text-gray-500 dark:text-gray-400">
                  <ChartBarIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>{t('noDataAvailable') || 'No data available for this period'}</p>
                  <p className="text-sm mt-1">{t('dataWillAppear') || 'Daily cost data will appear here after processing'}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Usage & Charts */}
          <div className="lg:col-span-2 space-y-6">
            {/* Usage Charts */}
            {usage && subscription && usageChartData && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('usageTrends')}</h3>
                  <Line
                    data={usageChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: {
                            color: document.documentElement.classList.contains('dark') ? '#9CA3AF' : '#4B5563',
                          },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            color: document.documentElement.classList.contains('dark') ? '#9CA3AF' : '#6B7280',
                          },
                          grid: {
                            color: document.documentElement.classList.contains('dark') ? '#374151' : '#E5E7EB',
                          },
                        },
                        x: {
                          ticks: {
                            color: document.documentElement.classList.contains('dark') ? '#9CA3AF' : '#6B7280',
                          },
                          grid: {
                            color: document.documentElement.classList.contains('dark') ? '#374151' : '#E5E7EB',
                          },
                        },
                      },
                    }}
                  />
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('overallUsage')}</h3>
                  {overallUsageData && (
                    <Doughnut
                      data={overallUsageData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                          legend: {
                            display: true,
                            position: 'bottom',
                            labels: {
                              color: document.documentElement.classList.contains('dark') ? '#9CA3AF' : '#4B5563',
                            },
                          },
                        },
                      }}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Usage Statistics */}
            {usage && subscription && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex items-center gap-2 mb-6">
                  <ChartBarIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('usageThisMonth')}</h3>
                </div>

                <div className="space-y-6">
                  {/* Metrics Events */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('metricsEvents')}</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {formatNumber(usage.metrics_events)} / {formatNumber(subscription.plan.included_metrics_events)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-3 rounded-full transition-all ${getUsageColor(
                          getUsagePercentage(usage.metrics_events, subscription.plan.included_metrics_events)
                        )}`}
                        style={{
                          width: `${getUsagePercentage(usage.metrics_events, subscription.plan.included_metrics_events)}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Log Events */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('logEvents')}</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {formatNumber(usage.log_events)} / {formatNumber(subscription.plan.included_log_events)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-3 rounded-full transition-all ${getUsageColor(
                          getUsagePercentage(usage.log_events, subscription.plan.included_log_events)
                        )}`}
                        style={{
                          width: `${getUsagePercentage(usage.log_events, subscription.plan.included_log_events)}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Trace Spans */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('traceSpans')}</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {formatNumber(usage.trace_spans)} / {formatNumber(subscription.plan.included_trace_spans)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-3 rounded-full transition-all ${getUsageColor(
                          getUsagePercentage(usage.trace_spans, subscription.plan.included_trace_spans)
                        )}`}
                        style={{
                          width: `${getUsagePercentage(usage.trace_spans, subscription.plan.included_trace_spans)}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Storage */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('storage')}</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {usage.storage_gb.toFixed(2)} GB / {subscription.plan.included_storage_gb} GB
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-3 rounded-full transition-all ${getUsageColor(
                          getUsagePercentage(usage.storage_gb, subscription.plan.included_storage_gb)
                        )}`}
                        style={{
                          width: `${getUsagePercentage(usage.storage_gb, subscription.plan.included_storage_gb)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {usage.overage_costs.total > 0 && (
                  <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                      ⚠️ Overage charges this month: {formatPrice(usage.overage_costs.total)}
                    </p>
                    <div className="text-xs text-yellow-700 dark:text-yellow-300 space-y-1">
                      {usage.overage_costs.metrics > 0 && (
                        <div>• Metrics: {formatPrice(usage.overage_costs.metrics)}</div>
                      )}
                      {usage.overage_costs.logs > 0 && <div>• Logs: {formatPrice(usage.overage_costs.logs)}</div>}
                      {usage.overage_costs.traces > 0 && (
                        <div>• Traces: {formatPrice(usage.overage_costs.traces)}</div>
                      )}
                      {usage.overage_costs.storage > 0 && (
                        <div>• Storage: {formatPrice(usage.overage_costs.storage)}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Invoices with Details */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <div className="flex items-center gap-2 mb-6">
                <DocumentTextIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('recentInvoices')}</h3>
              </div>

              {invoices.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-400 text-center py-8">{t('invoices.noInvoices')}</p>
              ) : (
                <div className="space-y-3">
                  {invoices.map((invoice) => (
                    <div key={invoice.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <div
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                        onClick={() => setExpandedInvoice(expandedInvoice === invoice.id ? null : invoice.id)}
                      >
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 dark:text-white">{invoice.invoice_number}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {new Date(invoice.issued_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {formatPrice(invoice.total)}
                          </span>
                          <span
                            className={`px-3 py-1 text-xs font-semibold rounded-full ${
                              invoice.status === 'PAID'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : invoice.status === 'PENDING'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}
                          >
                            {invoice.status}
                          </span>
                          {expandedInvoice === invoice.id ? (
                            <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>

                      {expandedInvoice === invoice.id && (
                        <div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                          <div className="space-y-3 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {formatPrice(invoice.subtotal)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Tax:</span>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {formatPrice(invoice.tax)}
                              </span>
                            </div>
                            <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                              <span className="font-semibold text-gray-900 dark:text-white">Total:</span>
                              <span className="font-semibold text-gray-900 dark:text-white">
                                {formatPrice(invoice.total)}
                              </span>
                            </div>
                            {invoice.amount_due > 0 && (
                              <div className="flex justify-between text-red-600 dark:text-red-400">
                                <span>Amount Due:</span>
                                <span className="font-semibold">{formatPrice(invoice.amount_due)}</span>
                              </div>
                            )}
                            {invoice.paid_at && (
                              <div className="flex justify-between text-green-600 dark:text-green-400">
                                <span>Paid on:</span>
                                <span>{new Date(invoice.paid_at).toLocaleDateString()}</span>
                              </div>
                            )}
                            {(() => {
                              const stripeUrl = (invoice as any).stripe_invoice_pdf;
                              const localUrl = (invoice as any).invoice_pdf_url
                                || `/api/v1/billing/invoices/${invoice.id}/pdf`;
                              const href = stripeUrl || localUrl;
                              return (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block mt-3 text-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                                >
                                  {t('invoices.downloadPdf', 'Download PDF Invoice')}
                                </a>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Payment Methods & Plan Info */}
          <div className="space-y-6">
            {/* Payment Methods */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('paymentMethods')}</h3>
                {subscription?.stripe_customer_id && (
                  <button
                    onClick={handleOpenStripePortal}
                    className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
                  >
                    + {paymentMethods.length > 0 ? t('changeCard') : t('addCard')}
                  </button>
                )}
              </div>

              {!subscription?.stripe_customer_id ? (
                <div className="text-center py-6">
                  <CreditCardIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t('stripeNotConfigured')}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    {t('paymentMethodsManaged')}
                  </p>
                </div>
              ) : paymentMethods.length === 0 ? (
                <div className="text-center py-6">
                  <CreditCardIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('noPaymentMethods')}</p>
                  <button
                    onClick={handleOpenStripePortal}
                    className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
                  >
                    Add your first card
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {paymentMethods.map((method) => (
                    <div
                      key={method.id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <CreditCardIcon className="h-8 w-8 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {getCardBrandIcon(method.card.brand)} •••• {method.card.last4}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            Expires {method.card.exp_month}/{method.card.exp_year}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Plan Features */}
            {subscription?.plan && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('planFeatures')}</h3>
                <ul className="space-y-3">
                  {subscription.plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
                      <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Plan Limits */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('planLimits')}</h3>
              {(subscription?.plan || paygData?.plan) ? (
                <div className="space-y-3 text-sm">
                  {subscription?.plan && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('applications')}</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {subscription.plan.max_applications === -1 ? t('unlimited') : subscription.plan.max_applications}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('users')}</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {subscription.plan.max_users === -1 ? t('unlimited') : subscription.plan.max_users}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('hosts')}</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {subscription.plan.max_hosts === -1 ? t('unlimited') : subscription.plan.max_hosts}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('dataRetention')}</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {subscription.plan.data_retention_days} {t('days')}
                        </span>
                      </div>
                    </>
                  )}
                  {!subscription?.plan && paygData?.plan && (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {t('currentPlan')}: <span className="font-semibold text-gray-900 dark:text-white">{paygData.plan.name}</span>
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        {t('usageBasedPricingDescription')}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('noPlanInfo')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
